import { useState, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { QrCode, Star, Trophy, ArrowRight, X } from 'lucide-react';

const STORAGE_KEY = 'scout_onboarded';

const steps = [
  {
    icon: QrCode,
    titleKey: 'onboarding.step1Title',
    descriptionKey: 'onboarding.step1Description',
    gradient: 'from-primary to-forest',
  },
  {
    icon: Star,
    titleKey: 'onboarding.step2Title',
    descriptionKey: 'onboarding.step2Description',
    gradient: 'from-gold to-gold/70',
  },
  {
    icon: Trophy,
    titleKey: 'onboarding.step3Title',
    descriptionKey: 'onboarding.step3Description',
    gradient: 'from-forest to-primary',
  },
];

const slideVariants = {
  enter: (direction: number) => ({
    x: direction > 0 ? 300 : -300,
    opacity: 0,
  }),
  center: {
    x: 0,
    opacity: 1,
  },
  exit: (direction: number) => ({
    x: direction > 0 ? -300 : 300,
    opacity: 0,
  }),
};

export function useOnboarding() {
  const [visible, setVisible] = useState(() => !localStorage.getItem(STORAGE_KEY));

  const dismiss = useCallback(() => {
    localStorage.setItem(STORAGE_KEY, '1');
    setVisible(false);
  }, []);

  return { showOnboarding: visible, dismissOnboarding: dismiss };
}

interface OnboardingOverlayProps {
  visible: boolean;
  onDismiss: () => void;
}

export default function OnboardingOverlay({ visible, onDismiss }: OnboardingOverlayProps) {
  const { t } = useTranslation('scout');
  const [[step, direction], setStep] = useState([0, 0]);
  const closeRef = useRef<HTMLButtonElement>(null);
  const isLast = step === steps.length - 1;
  const current = steps[step]!;
  const Icon = current.icon;

  // Focus close button when overlay opens
  useEffect(() => {
    if (visible) closeRef.current?.focus();
  }, [visible]);

  function next() {
    if (isLast) {
      onDismiss();
    } else {
      setStep([step + 1, 1]);
    }
  }

  function goToStep(index: number) {
    setStep([index, index > step ? 1 : -1]);
  }

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center px-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
        >
          <motion.div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />

          <motion.div
            role="dialog"
            aria-modal="true"
            aria-label={t('onboarding.step1Title')}
            className="relative w-full max-w-sm bg-white rounded-3xl shadow-2xl overflow-hidden"
            initial={{ scale: 0.9, opacity: 0, y: 30 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 30 }}
            transition={{ type: 'spring', damping: 20, stiffness: 300 }}
          >
            <button
              ref={closeRef}
              onClick={onDismiss}
              className="absolute top-4 end-4 z-10 p-1.5 rounded-full bg-black/5 hover:bg-black/10 transition-colors"
              aria-label={t('onboarding.close')}
            >
              <X className="w-4 h-4 text-muted-foreground" />
            </button>

            <div className="pt-10 pb-8 px-8 flex flex-col items-center">
              <div className="overflow-hidden w-full">
                <AnimatePresence mode="wait" custom={direction}>
                  <motion.div
                    key={step}
                    custom={direction}
                    variants={slideVariants}
                    initial="enter"
                    animate="center"
                    exit="exit"
                    transition={{ duration: 0.3, ease: [0.32, 0.72, 0, 1] }}
                    className="flex flex-col items-center text-center"
                  >
                    <motion.div
                      className={`w-20 h-20 rounded-2xl bg-gradient-to-br ${current.gradient} flex items-center justify-center shadow-lg mb-6`}
                      initial={{ scale: 0.5 }}
                      animate={{ scale: 1 }}
                      transition={{ type: 'spring', damping: 12, stiffness: 200, delay: 0.1 }}
                    >
                      <Icon className="w-9 h-9 text-white" strokeWidth={1.8} />
                    </motion.div>

                    <h2 className="text-xl font-bold text-foreground mb-2">{t(current.titleKey)}</h2>
                    <p className="text-small text-muted-foreground leading-relaxed max-w-[260px]">
                      {t(current.descriptionKey)}
                    </p>
                  </motion.div>
                </AnimatePresence>
              </div>

              <div className="flex items-center gap-2 mt-8 mb-6">
                {steps.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => goToStep(i)}
                    className={`rounded-full transition-all duration-300 ${
                      i === step ? 'w-6 h-2 bg-primary' : 'w-2 h-2 bg-primary/20 hover:bg-primary/40'
                    }`}
                    aria-label={`${i + 1}`}
                  />
                ))}
              </div>

              <button
                onClick={next}
                className="w-full py-3.5 rounded-xl bg-gradient-to-r from-primary to-forest text-white font-semibold text-small flex items-center justify-center gap-2 shadow-md active:scale-[0.98] transition-transform"
              >
                {isLast ? t('onboarding.getStarted') : t('onboarding.next')}
                {!isLast && <ArrowRight className="w-4 h-4" />}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
